import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

const LOGO_URL =
  "/vznlogo.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/" className="inline-flex w-full max-w-[14rem]">
            <img src={LOGO_URL} alt="Vision Grafix" className="w-full h-auto object-contain" />
          </Link>
        </div>

        {submitted ? (
          <div className="border border-[#262626] bg-[#0a0a0a] p-6" data-testid="forgot-success">
            <CheckCircle size={32} weight="fill" className="text-[#FF2D2D] mb-4" />
            <h2 className="font-display text-3xl mb-2 uppercase">Check your inbox.</h2>
            <p className="text-sm text-[#a3a3a3] mb-6">
              If an account exists for <strong className="text-white">{email}</strong>, we've sent a password reset link.
              The link expires in 30 minutes.
            </p>
            <p className="text-xs text-[#666] mb-6">Don't see it? Check your spam folder.</p>
            <Link
              to="/login"
              data-testid="forgot-back-to-login"
              className="btn-outline px-4 py-2 text-sm uppercase tracking-wider inline-flex items-center gap-2"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="forgot-form">
            <h2 className="font-display text-4xl mb-2 uppercase">Reset password</h2>
            <p className="text-sm text-[#a3a3a3] mb-8">
              Enter your account email and we'll send a link to reset your password.
            </p>

            {err && (
              <div className="mb-4 p-3 border border-[#ff2a00] bg-[#ff2a00]/10 text-sm text-[#ff8866]" data-testid="forgot-error">
                {err}
              </div>
            )}

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-2">
              <EnvelopeSimple size={12} /> Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none"
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-submit"
              className="btn-primary w-full mt-6 px-6 py-3 text-sm font-bold uppercase tracking-wider"
            >
              {loading ? "Sending…" : "Send reset link →"}
            </button>

            <Link
              to="/login"
              className="mt-6 text-sm text-[#a3a3a3] hover:text-white inline-flex items-center gap-2"
              data-testid="forgot-back"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
